/**
 * Contracts module API — Intake (plan §4.4, Appendix A "Intake", §13.4
 * WP-31). Typed calls over the shared axios instance (`services/api`), the
 * same convention `api/client.ts` uses — never axios directly (this repo's
 * jest cannot parse axios's ESM build unmocked; every module under
 * src/contracts reaches it only through the mocked services/api).
 *
 * A new, separate file rather than an addition to `api/client.ts`: another
 * WP appends to that file concurrently while this one runs (orchestrator
 * hand-off) — `intake.mount.md` notes a later one-line re-export from
 * `client.ts` as an optional follow-up, not something this file depends on.
 */
import api from '../../services/api';
import type { NegotiationKind, TagMatchCandidate, TagUnresolvedErrorBody } from './client';

/** plan §5.4 cta_intake_items.status. */
export type IntakeStatus = 'new' | 'approved' | 'ignored' | 'ingested' | 'failed';

/** WP-95: 'upload' for a row stored through the drop zone, 'drive' for the Drive
 *  watcher's 743-row backlog and everything after it (20260911041200_cta_intake_uploads.sql). */
export type IntakeSource = 'drive' | 'upload';

/** plan §5.4 cta_intake_items — one Drive watch-list row (or an uploaded item, §4.5). */
export interface IntakeItem {
  id: string;
  network_id: string;
  drive_file_id: string;
  drive_name: string;
  drive_mime: string | null;
  drive_path: string | null;
  drive_modified_at: string;
  drive_size: number | null;
  status: IntakeStatus;
  approved_by: string | null;
  approved_at: string | null;
  version_id: string | null;
  error: string | null;
  created_at: string;
  /** WP-95 columns (20260911041200_cta_intake_uploads.sql). A row upserted before that
   *  migration lands carries none of these — treat an absent `source` as 'drive' (the
   *  same value the migration's own DEFAULT gives every pre-existing row). */
  source: IntakeSource;
  storage_key: string | null;
  sha256: string | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
}

export type VersionDirection = 'received' | 'sent' | 'internal';
export type VersionSource = 'drive' | 'upload' | 'export';

/** plan §5.4 cta_versions — what approve/upload return alongside the study, negotiation and item. */
export interface IntakeVersion {
  id: string;
  document_id: string;
  version_no: number;
  direction: VersionDirection;
  round: number | null;
  version_date: string;
  filename: string;
  mime: string;
  storage_key: string;
  sha256: string;
  size_bytes: number | null;
  source: VersionSource;
  is_executed: boolean;
  parse_status: string;
  drive_file_id: string | null;
  drive_modified_at: string | null;
  created_at: string;
}

/** plan §5.4 cta_documents.kind. */
export type DocumentKind =
  | 'budget_workbook'
  | 'cta'
  | 'budget_exhibit'
  | 'protocol'
  | 'letter'
  | 'email'
  | 'remittance'
  | 'other';

/** The minimum a brand-new study needs (matches tabs/studies/CreateStudyModal.tsx's StudyInput shape, without importing that component). */
export interface IntakeNewStudyInput {
  protocolNumber: string;
  status: string;
  title?: string;
  sponsor?: string;
  cro?: string;
  therapeuticArea?: string;
  indication?: string;
}

/** The canonical type lives on api/client.ts; re-exported here under the same name so existing imports from './intake' keep working (MOUNT-2 wiring B). */
export type { NegotiationKind };

/** The minimum a brand-new negotiation needs. */
export interface IntakeNewNegotiationInput {
  kind: NegotiationKind;
  label?: string;
}

/** The fields POST /intake/:id/approve and POST /intake/upload share (plan §13.4 WP-31). */
export interface IntakeAssignmentInput {
  studyId?: string;
  newStudy?: IntakeNewStudyInput;
  negotiationId?: string;
  newNegotiation?: IntakeNewNegotiationInput;
  documentKind: DocumentKind;
  direction: VersionDirection;
  round: number;
  versionDate: string;
}

export interface ApproveResult {
  item: IntakeItem;
  study: { id: string; protocol_number: string; [key: string]: unknown };
  negotiation: { id: string; kind: NegotiationKind; [key: string]: unknown };
  version: IntakeVersion;
}

/** GET /intake — always scoped to the caller's network server-side; optional ?status filter. */
export async function listIntake(status?: IntakeStatus): Promise<IntakeItem[]> {
  const response = await api.get<{ items: IntakeItem[] }>('/api/contracts/intake', {
    params: status ? { status } : undefined,
  });
  return response.data.items ?? [];
}

/** POST /intake/:id/approve — resolve/create the study and negotiation, download, store, version, queue its parse. */
export async function approveIntakeItem(id: string, input: IntakeAssignmentInput): Promise<ApproveResult> {
  const response = await api.post<ApproveResult>(`/api/contracts/intake/${id}/approve`, input);
  return response.data;
}

/** POST /intake/:id/ignore — recorded, never deleted (plan §4.4). */
export async function ignoreIntakeItem(id: string, reason: string): Promise<IntakeItem> {
  const response = await api.post<{ item: IntakeItem }>(`/api/contracts/intake/${id}/ignore`, { reason });
  return response.data.item;
}

/**
 * POST /intake/upload — multipart form (field 'file'), the second intake
 * path for an email attachment that never passed through the Drive folder
 * (plan §4.5). `newStudy`/`newNegotiation` ride as JSON-encoded fields; the
 * backend parses them back into the same shape the JSON approve body uses.
 */
export async function uploadIntakeItem(file: File, input: IntakeAssignmentInput): Promise<ApproveResult> {
  const form = new FormData();
  form.append('file', file);
  if (input.studyId) form.append('studyId', input.studyId);
  if (input.newStudy) form.append('newStudy', JSON.stringify(input.newStudy));
  if (input.negotiationId) form.append('negotiationId', input.negotiationId);
  if (input.newNegotiation) form.append('newNegotiation', JSON.stringify(input.newNegotiation));
  form.append('documentKind', input.documentKind);
  form.append('direction', input.direction);
  form.append('round', String(input.round));
  form.append('versionDate', input.versionDate);

  const response = await api.post<ApproveResult>('/api/contracts/intake/upload', form);
  return response.data;
}

// ---------------------------------------------------------------------------
// WP-95 (owner decision, Jordan, 2026-09-17): the intake drop zone. One file
// stored the moment it lands (POST /intake/files), assignment fields proposed
// from the file name (POST /intake/proposals, never stored — decision 8), and
// bulk approval with per-row results (POST /intake/approve-bulk). `listIntake`
// above is untouched; `listIntakePage` is what IntakeTab now reads through so
// a large list never hits the 1000-row PostgREST clamp (§16.1).
// ---------------------------------------------------------------------------

/**
 * G8: every proposed value is INFERRED and says so. `rule` and `evidence` are what the
 * UI shows on hover, so a person can see WHY the box was pre-filled.
 */
export interface ProposedValue<T> {
  value: T;
  label: 'inferred';
  rule: string; // e.g. 'P1_code_series'
  evidence: string; // the exact substring of the file name the rule read
}

/** A field two rules disagreed about: nothing is proposed for it (conservative by design). */
export interface ProposalConflict {
  field: 'protocolNumber' | 'documentKind' | 'direction' | 'round' | 'versionDate';
  reason: string;
  hits: string[];
}

/** POST /intake/files and POST /intake/proposals both return this per row — computed from
 *  drive_name on the server, on demand, and never stored (decision 8). */
export interface FilenameProposal {
  intakeItemId: string;
  filename: string;
  protocolNumber: ProposedValue<string> | null;
  /** The master-list ENTRY ID, only when the leading token resolved exactly or through an alias. */
  sponsor: (ProposedValue<string> & { display: string }) | null;
  /** Offered, never applied (G11): what the person may click when `sponsor` is null. */
  sponsorCandidates: TagMatchCandidate[];
  documentKind: ProposedValue<DocumentKind> | null;
  negotiationKind: ProposedValue<NegotiationKind> | null;
  direction: ProposedValue<VersionDirection> | null;
  round: ProposedValue<number> | null;
  versionDate: ProposedValue<string> | null;
  /** Recognised but non-proposing tokens: FINAL, the WCR signer's name, fully-executed. */
  markers: { final: boolean; wcrSigner: boolean; fullyExecuted: boolean };
  conflicts: ProposalConflict[];
  /** G9: what the rules read and what they did not. */
  inventory: { tokens: string[]; consumed: string[] };
}

export interface BulkApproveOk {
  intakeItemId: string;
  ok: true;
  item: IntakeItem;
  study: { id: string; protocol_number: string; [k: string]: unknown };
  negotiation: { id: string; kind: NegotiationKind; [k: string]: unknown };
  version: IntakeVersion;
}

export interface BulkApproveFailed {
  intakeItemId: string;
  ok: false;
  status: number;
  code?: string;
  error: string;
  field?: TagUnresolvedErrorBody['field'];
  candidates?: TagMatchCandidate[];
}

export type BulkApproveResult = BulkApproveOk | BulkApproveFailed;

export interface BulkApproveResponse {
  summary: { requested: number; succeeded: number; failed: number };
  results: BulkApproveResult[];
}

export interface IntakePage {
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** One row of POST /intake/approve-bulk's `assignments` array — the same shape
 *  POST /intake/:id/approve takes, plus which row it is for. */
export interface BulkAssignment extends IntakeAssignmentInput {
  intakeItemId: string;
}

/**
 * POST /intake/files — the drop (plan/ctx-WP-95 PART A1). One HTTP request per
 * dropped file, field name exactly 'file' ([OWNER] 230: matches uploadMiddleware
 * exactly, so one 25MB file failing never takes the rest of a batch with it).
 * The response's `item` is the complete row, so the caller appends it with no
 * refetch, and `proposal` is this same row's inferred fields, computed once.
 */
export async function storeIntakeFile(file: File): Promise<{ item: IntakeItem; proposal: FilenameProposal }> {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post<{ item: IntakeItem; proposal: FilenameProposal }>('/api/contracts/intake/files', form);
  return response.data;
}

/**
 * POST /intake/proposals — re-propose for rows already stored (PART A2). 1 to
 * 200 ids; a row outside the caller's network reads exactly like an id naming
 * no row at all (no existence oracle) and comes back in `missing`.
 */
export async function proposeForIntakeItems(
  intakeItemIds: string[]
): Promise<{ proposals: FilenameProposal[]; missing: string[] }> {
  const response = await api.post<{ proposals: FilenameProposal[]; missing: string[] }>(
    '/api/contracts/intake/proposals',
    { intakeItemIds }
  );
  return response.data;
}

/**
 * POST /intake/approve-bulk — up to BULK_APPROVE_MAX (25, [OWNER] 231) assignments
 * per request. Answers 200 whenever the envelope itself is valid; each assignment
 * succeeds or fails independently in `results`, always in request order (§16.1:
 * no cross-call transactions — a row's failure never stops or undoes another).
 */
export async function approveIntakeBulk(assignments: BulkAssignment[]): Promise<BulkApproveResponse> {
  const response = await api.post<BulkApproveResponse>('/api/contracts/intake/approve-bulk', { assignments });
  return response.data;
}

/**
 * GET /intake, paged (PART A4 — backwards compatible: `items` keeps its name
 * and shape). `listIntake` above is unchanged for any existing caller;
 * IntakeTab reads through this one instead so a large list never hits the
 * 1000-row PostgREST clamp (§16.1). Default limit 200, max 500, server-enforced
 * — the default (status unset, offset 0) sends no query params at all, the
 * same as `listIntake()`'s own no-status call.
 */
export async function listIntakePage(
  opts: { status?: IntakeStatus; limit?: number; offset?: number } = {}
): Promise<{ items: IntakeItem[]; page: IntakePage }> {
  const params: { status?: IntakeStatus; limit?: number; offset?: number } = {};
  if (opts.status) params.status = opts.status;
  if (opts.limit !== undefined) params.limit = opts.limit;
  if (opts.offset !== undefined) params.offset = opts.offset;
  const response = await api.get<{ items?: IntakeItem[]; page?: IntakePage }>('/api/contracts/intake', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  // WP-95b fix (blocking defect 3): unlike listIntake above, this used to
  // return response.data raw. A body with no items (e.g. a mock/fallback
  // that answers `{ data: [] }`) then made the caller destructure undefined
  // out of `items` and crash on render. PART A4 names listIntake's `?? []`
  // as the reason the paged contract stays backwards compatible; restore the
  // same defensiveness here for both keys.
  const data = response.data ?? ({} as { items?: IntakeItem[]; page?: IntakePage });
  return { items: data.items ?? [], page: data.page ?? { limit: opts.limit ?? 200, offset: opts.offset ?? 0, hasMore: false } };
}
